import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import CategoryForm from './components/CategoryForm';
import ServiceForm from './components/ServiceForm';
import { categoryService } from '../../services/categoryService';
import {
  clearCatalogCache,
  getAllServices,
  removeService,
  saveService,
  type Service,
  type Category,
} from '../../services/servicesStore';
import { currencyService } from '../../services/currencyService';
import { isSupabaseConfigured } from '../../lib/supabase';
import {
  getServiceBillingLabel,
  getServiceOptionsSummary,
  getServicePricePrefix,
  hasStartingPrice,
} from '../../services/serviceCatalogUtils';

const PAGE_SIZE_OPTIONS: Array<number | 'all'> = [12, 24, 48, 'all'];

const ServiceManager = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [displayCountry, setDisplayCountry] = useState(currencyService.getCountry());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number | 'all'>('all');
  const canManageStandaloneCategories = isSupabaseConfigured();

  const loadCatalog = async () => {
    setIsLoading(true);

    try {
      const [servicesData, categoriesData] = await Promise.all([
        getAllServices(true),
        categoryService.getCategories(),
      ]);

      setServices(servicesData);
      setCategories(categoriesData);
    } catch (error) {
      console.error(error);
      toast.error('Erreur de chargement du catalogue');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCatalog().catch((error) => {
      console.error(error);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    return currencyService.subscribe((nextCountry) => {
      setDisplayCountry(nextCountry);
    });
  }, []);

  const displayCurrencyCode = currencyService.getDisplayCurrencyCode(displayCountry);
  const filteredServices = services.filter((service) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !normalizedSearch ||
      service.name.toLowerCase().includes(normalizedSearch) ||
      service.description.toLowerCase().includes(normalizedSearch) ||
      service.category.toLowerCase().includes(normalizedSearch) ||
      service.id.toLowerCase().includes(normalizedSearch);

    const matchesCategory =
      selectedCategory === 'all' || service.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const effectivePageSize =
    pageSize === 'all' ? Math.max(filteredServices.length, 1) : pageSize;
  const totalPages = Math.max(1, Math.ceil(filteredServices.length / effectivePageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStartIndex = (safeCurrentPage - 1) * effectivePageSize;
  const paginatedServices = filteredServices.slice(
    pageStartIndex,
    pageStartIndex + effectivePageSize
  );
  const pageWindowStart = Math.max(1, safeCurrentPage - 2);
  const pageWindowEnd = Math.min(totalPages, pageWindowStart + 4);
  const visiblePageNumbers = Array.from(
    { length: pageWindowEnd - pageWindowStart + 1 },
    (_, index) => pageWindowStart + index
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSubmit = async (data: Service) => {
    try {
      await saveService(data);
      clearCatalogCache();
      await loadCatalog();
      setShowForm(false);
      setEditingService(null);
      setCurrentPage(1);
      toast.success('Service sauvegardé !');
    } catch (error) {
      console.error(error);
      toast.error('Erreur pendant la sauvegarde du service');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer ce service ?')) {
      return;
    }

    try {
      await removeService(id);
      clearCatalogCache();
      await loadCatalog();
      setCurrentPage(1);
      toast.success('Service supprimé !');
    } catch (error) {
      console.error(error);
      toast.error('Erreur pendant la suppression');
    }
  };

  const handleCategorySubmit = async (data: { name: string }) => {
    try {
      await categoryService.addCategory(data.name);
      clearCatalogCache();
      await loadCatalog();
      setShowCategoryForm(false);
      toast.success('Catégorie ajoutée !');
    } catch (error) {
      console.error(error);
      toast.error('Erreur pendant la création de catégorie');
    }
  };

  const handleCategoryDelete = async (id: string) => {
    try {
      await categoryService.deleteCategory(id);
      clearCatalogCache();
      await loadCatalog();
      toast.success('Catégorie supprimée !');
    } catch (error) {
      console.error(error);
      toast.error('Impossible de supprimer cette catégorie');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-2xl font-bold">Gestion des Services</h2>
        <div className="flex gap-4">
          {canManageStandaloneCategories && (
            <button
              onClick={() => setShowCategoryForm(true)}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Nouvelle Catégorie
            </button>
          )}
          <button
            onClick={() => {
              setEditingService(null);
              setShowForm(true);
            }}
            className="bg-neon-orange text-white px-4 py-2 rounded-lg hover:bg-[#FF4500] transition-colors"
          >
            Nouveau Service
          </button>
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Catégories</h3>
        {!canManageStandaloneCategories && (
          <p className="mb-4 text-sm text-gray-500">
            Sur cet hebergement, les categories suivent directement le catalogue. Tu peux en
            creer une nouvelle en la saisissant dans le formulaire d&apos;un service.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {categories.map((category) => (
            <div
              key={category.id}
              className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 flex justify-between items-center"
            >
              <span>{category.name}</span>
              {canManageStandaloneCategories ? (
                <button
                  onClick={() => handleCategoryDelete(category.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  Supprimer
                </button>
              ) : (
                <span className="text-xs uppercase tracking-[0.18em] text-gray-400">Catalogue</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-4">
          <h3 className="text-xl font-semibold">Services</h3>
          <p className="text-sm text-gray-500 mt-1">
            Affichage catalogue admin actuel: {displayCurrencyCode}. La base d&apos;édition reste en EUR.
          </p>
        </div>

        <div className="mb-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Recherche</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Nom, id, catégorie, description..."
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Catégorie</label>
            <select
              value={selectedCategory}
              onChange={(event) => setSelectedCategory(event.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
            >
              <option value="all">Toutes les catégories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
          <div className="text-sm text-gray-600">
            {filteredServices.length === 0
              ? 'Aucun service à afficher.'
              : `Affichage ${pageStartIndex + 1}-${Math.min(
                  pageStartIndex + paginatedServices.length,
                  filteredServices.length
                )} sur ${filteredServices.length} service${filteredServices.length > 1 ? 's' : ''}.`}
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Par page</label>
              <select
                value={pageSize}
                onChange={(event) =>
                  setPageSize(
                    event.target.value === 'all' ? 'all' : Number(event.target.value)
                  )
                }
                className="rounded-xl border border-gray-300 px-3 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option === 'all' ? 'Tout afficher' : option}
                  </option>
                ))}
              </select>
            </div>

            {filteredServices.length > effectivePageSize && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, safeCurrentPage - 1))}
                  disabled={safeCurrentPage === 1}
                  className="rounded-xl border border-gray-300 px-3 py-2 text-sm text-gray-700 transition-colors hover:border-neon-orange hover:text-neon-orange disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Précédent
                </button>

                <div className="flex items-center gap-2">
                  {visiblePageNumbers.map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`min-w-[40px] rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                        safeCurrentPage === pageNumber
                          ? 'bg-neon-orange text-white'
                          : 'border border-gray-300 text-gray-700 hover:border-neon-orange hover:text-neon-orange'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, safeCurrentPage + 1))}
                  disabled={safeCurrentPage === totalPages}
                  className="rounded-xl border border-gray-300 px-3 py-2 text-sm text-gray-700 transition-colors hover:border-neon-orange hover:text-neon-orange disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-gray-500">Services au catalogue</div>
            <div className="mt-2 text-3xl font-bold text-gray-900">{services.length}</div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-gray-500">Offres à partir de</div>
            <div className="mt-2 text-3xl font-bold text-neon-orange">
              {services.filter((service) => hasStartingPrice(service)).length}
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-gray-500">Résultats affichés</div>
            <div className="mt-2 text-3xl font-bold text-gray-900">{filteredServices.length}</div>
          </div>
        </div>

        {isLoading ? (
          <p className="text-gray-600">Chargement...</p>
        ) : filteredServices.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-600">
            Aucun service ne correspond aux filtres actuels.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedServices.map((service) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="overflow-hidden rounded-2xl bg-white shadow-lg ring-1 ring-gray-100"
              >
                {service.image && (
                  <img
                    src={service.image}
                    alt={service.name}
                    className="h-48 w-full object-cover"
                  />
                )}
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">
                        {service.category}
                      </div>
                      <h3 className="text-xl font-semibold">{service.name}</h3>
                    </div>
                    {service.popular && (
                      <span className="bg-neon-orange text-white text-sm px-2 py-1 rounded-full">
                        Populaire
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <div className="mb-4 rounded-xl border border-gray-100 bg-gray-50 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">
                      {getServicePricePrefix(service)}
                    </div>
                    <div className="mt-2">
                      <span className="text-2xl font-bold text-neon-orange">
                        {currencyService.formatAmountForDisplay(service.price, 'EUR', {
                          country: displayCountry,
                          display: 'code',
                        })}
                      </span>
                      <div className="mt-1 text-sm text-gray-500">
                        {getServiceBillingLabel(service)}
                      </div>
                      {(service.pricingNote || service.options?.length) && (
                        <div className="mt-2 text-sm text-gray-500">
                          {getServiceOptionsSummary(service) || service.pricingNote}
                        </div>
                      )}
                      {displayCurrencyCode !== 'EUR' && (
                        <div className="text-xs text-gray-400 mt-1">
                          Base catalogue: {currencyService.formatCurrencyAmount(service.price, 'EUR', { display: 'code' })}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mb-4 flex flex-wrap gap-2">
                    {service.features.slice(0, 2).map((feature, index) => (
                      <span
                        key={`${service.id}-feature-${index}`}
                        className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600"
                      >
                        {feature}
                      </span>
                    ))}
                    <span className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600">
                      ID: {service.id}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingService(service);
                        setShowForm(true);
                      }}
                      className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => handleDelete(service.id)}
                      className="flex-1 bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {!isLoading && filteredServices.length > effectivePageSize && (
          <div className="mt-8 flex justify-center">
            <div className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
              <button
                onClick={() => setCurrentPage(Math.max(1, safeCurrentPage - 1))}
                disabled={safeCurrentPage === 1}
                className="rounded-xl px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Précédent
              </button>
              <span className="px-3 text-sm font-medium text-gray-600">
                Page {safeCurrentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, safeCurrentPage + 1))}
                disabled={safeCurrentPage === totalPages}
                className="rounded-xl px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {showCategoryForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowCategoryForm(false)}
        >
          <CategoryForm
            onSubmit={handleCategorySubmit}
            onCancel={() => setShowCategoryForm(false)}
          />
        </div>
      )}

      {showForm && (
        <ServiceForm
          initialData={editingService ?? undefined}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingService(null);
          }}
          categories={categories.map((category) => category.name)}
        />
      )}
    </div>
  );
};

export default ServiceManager;
